import getUserInfo from "./user-auth/UserInfo";
import { v4 as uuidv4 } from "uuid";

/**
 * Timeout configuration for Acordos e Contratos API
 * Adjust this value if backend processing typically takes longer
 */
const ACORDOS_TIMEOUT_MS = 300000; // 5 minutes (300000ms)
const LONG_REQUEST_WARNING_MS = 60000; // 1 minute - when to warn about long request

/**
 * Sends a message to the Acordos e Contratos service (n8n workflow)
 *
 * IMPORTANT: For the first message in a conversation, this function automatically generates
 * a UUID as the session_id to prevent "unknown-session" responses from the API.
 * The backend returns this session_id as the protocol, which should be used for subsequent messages.
 *
 * @param {string} message - The user's message
 * @param {string|object} sessionId - The existing session ID (chat protocol) or empty/object for new sessions
 * @returns {Promise} - Promise resolving to the Acordos response with {answer, protocol, historic, knowledge, suggestions, summary}
 */
export const sendAcordosMessage = async (message, sessionId = "") => {
  return new Promise((resolve, reject) => {
    try {
      // Get user information
      const userInfo = getUserInfo();

      if (!userInfo) {
        reject(new Error("User is not authenticated"));
        return;
      }

      const { email } = userInfo;

      // Ensure we have a proper session ID string
      let chatSessionId;

      // Check if sessionId is empty, null, or an empty object
      if (
        !sessionId ||
        sessionId === "" ||
        (typeof sessionId === "object" && Object.keys(sessionId).length === 0)
      ) {
        // Generate a new UUID if no valid sessionId is provided
        chatSessionId = uuidv4();
      } else if (typeof sessionId === "object") {
        // If it's a non-empty object, stringify it (should not normally happen)
        chatSessionId = JSON.stringify(sessionId);
      } else {
        // Use the existing string sessionId
        chatSessionId = sessionId;
      }

      // Get n8n webhook URL from environment
      const apiUrl = process.env.REACT_APP_ACORDOS_API_URL;

      // DEBUG LOGS - API URL check

      if (!apiUrl) {
        reject(new Error("API URL for Acordos is not configured"));
        return;
      }


      // Create headers
      const headers = new Headers();
      headers.append("Content-Type", "application/json");

      // Create request body matching n8n workflow input
      const requestBody = {
        message: message,
        session_id: chatSessionId,
        sender_name: email,
      };

      // Create AbortController for timeout
      const controller = new AbortController();
      
      // Warning after 1 minute
      const warningTimeoutId = setTimeout(() => {
      }, LONG_REQUEST_WARNING_MS);
      
      // Abort after configured timeout
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, ACORDOS_TIMEOUT_MS);

      // Create request options
      const requestOptions = {
        method: "POST",
        headers: headers,
        body: JSON.stringify(requestBody),
        redirect: "follow",
        signal: controller.signal, // Add abort signal
      };

      // Make the API call
      fetch(apiUrl, requestOptions)
        .then((response) => {
          clearTimeout(timeoutId); // Clear timeout on response
          clearTimeout(warningTimeoutId); // Clear warning timeout
          if (!response.ok) {
            return response.text().then((text) => {
              try {
                // Try to parse as JSON for better error details
                const errorData = JSON.parse(text);
                throw new Error(
                  `HTTP error! Status: ${response.status}, Details: ${JSON.stringify(errorData)}`
                );
              } catch (e) {
                // If not JSON, use the text response
                throw new Error(
                  `HTTP error! Status: ${response.status}, Response: ${text}`
                );
              }
            });
          }
          return response.json();
        })
        .then((data) => {
          // n8n workflow already returns the correct format
          // {answer, protocol, historic, knowledge, suggestions, summary}
          resolve(data);
        })
        .catch((error) => {
          clearTimeout(timeoutId); // Clear timeout on error
          clearTimeout(warningTimeoutId); // Clear warning timeout
          
          // Check if error is due to timeout
          if (error.name === 'AbortError') {
            reject(new Error(`A requisição excedeu o tempo limite de ${ACORDOS_TIMEOUT_MS / 60000} minutos. O backend pode estar processando uma consulta complexa. Tente novamente mais tarde.`));
          } else {
            reject(error);
          }
        });
    } catch (error) {
      reject(error);
    }
  });
};

